// Optional Sentry browser error tracking. Bundled via Vite (like auth.js)
// rather than injected from a runtime CDN: no external host to trust or pin
// an SRI hash against, npm's install-time integrity check already covers
// this dependency. No-op until sentry-config.js sets a real DSN.
import * as Sentry from '@sentry/browser';

// A share token IS the capability for an unlisted share — anyone holding one
// can read that workout. Sentry's default fetch/history breadcrumbs record
// full URLs, so `/s/<token>` and `/api/shares/<token>` would put live
// capability URLs into every error report. Keep the shape of the path (useful
// for debugging routing) and drop the secret.
const SHARE_TOKEN_PATH = /\/(s|api\/shares)\/[^/?#]+/g;

function redactShareTokens(value) {
  if (typeof value !== 'string') return value;
  return value.replace(SHARE_TOKEN_PATH, (_match, prefix) => `/${prefix}/[token]`);
}

// Error diagnostics are the one non-essential thing the site does, so they
// are the one thing the "Manage cookies" panel (site.js) actually controls.
// Opt-out rather than opt-in: no cookies or identifiers are set, reports
// carry no PII, and the basis is legitimate interest — but the choice has to
// be real and it has to be honoured here, before init, or the panel is
// theatre. A throwing localStorage (private mode, blocked site data) falls
// back to enabled, matching the documented default.
const DIAGNOSTICS_KEY = 'fyrvo.privacy.diagnostics';

function diagnosticsAllowed() {
  try {
    return window.localStorage.getItem(DIAGNOSTICS_KEY) !== 'off';
  } catch (e) {
    return true;
  }
}

const dsn = window.FYRVO_SENTRY_DSN;
if (dsn && diagnosticsAllowed()) {
  Sentry.init({
    dsn,
    environment: window.FYRVO_SENTRY_ENVIRONMENT || 'production',
    // The site is read-only marketing plus public profiles, so there is little
    // user content to leak — but state it rather than assume it, so a future
    // SDK default can't start attaching IPs or DOM text.
    sendDefaultPii: false,
    beforeBreadcrumb(breadcrumb) {
      const data = breadcrumb && breadcrumb.data;
      if (data) {
        // fetch/xhr use `url`; history navigation uses `from` / `to`.
        for (const key of ['url', 'from', 'to']) {
          if (data[key]) data[key] = redactShareTokens(data[key]);
        }
      }
      if (breadcrumb && breadcrumb.message) {
        breadcrumb.message = redactShareTokens(breadcrumb.message);
      }
      return breadcrumb;
    },
    beforeSend(event) {
      // Breadcrumbs are already scrubbed by beforeBreadcrumb above (Sentry
      // applies it at record time, before they're ever attached to an
      // event) — this covers the fields beforeBreadcrumb can't reach: the
      // request URL Sentry captures for the event itself, and any exception
      // message. A thrown `new Error('fetch failed: ' + url)` for a share
      // endpoint would otherwise ship the live capability URL verbatim.
      if (event.request && event.request.url) {
        event.request.url = redactShareTokens(event.request.url);
      }
      if (typeof event.message === 'string') {
        event.message = redactShareTokens(event.message);
      }
      const values = event.exception && event.exception.values;
      if (Array.isArray(values)) {
        for (const entry of values) {
          if (entry && typeof entry.value === 'string') {
            entry.value = redactShareTokens(entry.value);
          }
        }
      }
      return event;
    },
  });
}

// Let the preferences panel switch diagnostics off for the *current* page
// too, not just the next load — otherwise opting out appears to do nothing
// until navigation. Turning them back on needs a reload (Sentry can't be
// re-inited on a closed client), which the panel tells the user.
window.fyrvoDiagnostics = {
  isEnabled: diagnosticsAllowed,
  stop() {
    const client = Sentry.getClient();
    if (client) client.close();
  },
};
